import React, { useMemo } from 'react';
import { ScrollView } from 'react-native';
import { Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import type { GoodsPiece } from 'shared/src/api/goodsService';
import { useGetMeasureValues } from '../hooks/useGetMeasureValues';

const MIN_CELL_WIDTH = 64;
const CELL_HEIGHT = 36;
const CELL_PADDING_H = 8;
const CELL_PADDING_V = 10;
const STICKY_THRESHOLD = 4;

interface Props {
  piece: GoodsPiece;
}

const Cell = ({
  children,
  isHead,
  isFirstCol,
  width,
}: {
  children: React.ReactNode;
  isHead?: boolean;
  isFirstCol?: boolean;
  width?: number;
}) => (
  <Flex.Center
    width={width}
    minWidth={MIN_CELL_WIDTH}
    height={CELL_HEIGHT}
    padding={{ horizontal: CELL_PADDING_H, vertical: CELL_PADDING_V }}
    borderTopWidth={isHead ? 1 : undefined}
    borderRightWidth={1}
    borderBottomWidth={1}
    borderLeftWidth={isFirstCol ? 1 : 0}
    borderColor={'border_secondary'}
    backgroundColor={isHead ? 'bg_secondary' : 'bg_primary'}
  >
    {children}
  </Flex.Center>
);

export const MeasurementSizeDetailTable: React.FC<Props> = ({ piece }) => {
  const getMeasureValues = useGetMeasureValues();
  const { measureValues: tableHeadColumns, sizes } = getMeasureValues(piece);
  const { width: screenWidth } = useScreenSize();

  const headers = sizes.map(size => size.name);
  const columnCount = headers.length + 1;
  const needsScroll = headers.length >= STICKY_THRESHOLD;

  const cellWidth = useMemo(() => {
    if (needsScroll) {
      return undefined;
    }
    return Math.max(MIN_CELL_WIDTH, screenWidth / columnCount);
  }, [needsScroll, screenWidth, columnCount]);

  if (tableHeadColumns.length === 0) {
    return null;
  }

  const renderHeaderRow = () => (
    <Flex.Row>
      <Cell isHead isFirstCol width={cellWidth}>
        <Typography type={'subtitle3'} color={'content_primary'}>
          사이즈
        </Typography>
      </Cell>
      {headers.map(header => (
        <Cell isHead key={header} width={cellWidth}>
          <Typography type={'subtitle3'} color={'content_primary'}>
            {header}
          </Typography>
        </Cell>
      ))}
    </Flex.Row>
  );

  const renderDataRows = () =>
    tableHeadColumns.map(column => (
      <Flex.Row key={column.spot.name}>
        <Cell isFirstCol width={cellWidth}>
          <Typography
            type={'body3'}
            color={'content_primary'}
            numberOfLines={1}
          >
            {column.spot.name}
          </Typography>
        </Cell>
        {headers.map(header => {
          const matchedSize = sizes.find(size => size.name === header);
          const measureValue = matchedSize?.measure_values?.find(
            mv => mv.spot.name === column.spot.name,
          );

          return (
            <Cell key={header} width={cellWidth}>
              <Typography type={'body3'} color={'content_primary'}>
                {measureValue?.value ?? '-'}
              </Typography>
            </Cell>
          );
        })}
      </Flex.Row>
    ));

  if (!needsScroll) {
    return (
      <Flex borderTopWidth={1} borderColor={'border_secondary'}>
        {renderHeaderRow()}
        {renderDataRows()}
      </Flex>
    );
  }

  const renderStickyColumn = () => (
    <Flex>
      <Cell isHead isFirstCol>
        <Typography type={'subtitle3'} color={'content_primary'}>
          사이즈
        </Typography>
      </Cell>
      {tableHeadColumns.map(column => (
        <Cell isFirstCol key={column.spot.name}>
          <Typography
            type={'body3'}
            color={'content_primary'}
            numberOfLines={1}
          >
            {column.spot.name}
          </Typography>
        </Cell>
      ))}
    </Flex>
  );

  const renderScrollableColumns = () => (
    <Flex>
      <Flex.Row>
        {headers.map(header => (
          <Cell isHead key={header}>
            <Typography type={'subtitle3'} color={'content_primary'}>
              {header}
            </Typography>
          </Cell>
        ))}
      </Flex.Row>
      {tableHeadColumns.map(column => (
        <Flex.Row key={column.spot.name}>
          {headers.map(header => {
            const matchedSize = sizes.find(size => size.name === header);
            const measureValue = matchedSize?.measure_values?.find(
              mv => mv.spot.name === column.spot.name,
            );

            return (
              <Cell key={header}>
                <Typography type={'body3'} color={'content_primary'}>
                  {measureValue?.value ?? '-'}
                </Typography>
              </Cell>
            );
          })}
        </Flex.Row>
      ))}
    </Flex>
  );

  return (
    <Flex>
      <Flex.Row>
        {renderStickyColumn()}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
        >
          {renderScrollableColumns()}
        </ScrollView>
      </Flex.Row>
    </Flex>
  );
};
